'use client'

import { ReactNode, useEffect, useLayoutEffect, useRef, useState } from 'react'

import { Table as RadixTable } from '@radix-ui/themes'

import { useTableContext, useTableStore } from '../context/table-context'
import { StyledFooterRow } from '../styled'
import { getCompleteFilters, serializeAppliedFilters, useTableLayout } from './table-utils'

const TableSummaryRow = <T,>() => {
    const { calculateSummary } = useTableContext<T>()
    const { columns, leadingCells, hasActionsColumn, leadingCellStyle, columnStyle } = useTableLayout<T>()
    const filters = useTableStore<T, 'filters'>('filters')

    const [footerValues, setFooterValues] = useState<Map<string, ReactNode>>(new Map())

    // the `columns` prop is usually a new array on every parent render; recalculate only when the
    // calculated columns or the applied filters change, with the latest columns / calculateSummary
    const latest = useRef({ columns, filters, calculateSummary })
    useLayoutEffect(() => {
        latest.current = { columns, filters, calculateSummary }
    })

    const calculatedColumnsKey = columns
        .filter(column => column.footer?.calculate)
        .map(column => column.accessor)
        .join('|')
    const appliedFilters = serializeAppliedFilters(filters)

    useEffect(() => {
        if (!calculatedColumnsKey)
            return

        let cancelled = false
        const calculateFooters = async () => {
            const { columns, filters, calculateSummary } = latest.current
            const newFooterValues = new Map<string, ReactNode>()

            const calculatedData = await calculateSummary?.(getCompleteFilters(filters))

            for (const column of columns) {
                if (column.footer?.calculate) {
                    try {
                        const value = await column.footer.calculate(calculatedData)
                        newFooterValues.set(column.accessor as string, value)
                    }
                    catch (error) {
                        console.error(`Error calculating footer for ${String(column.accessor)}:`, error)
                        newFooterValues.set(column.accessor as string, null)
                    }
                }
            }

            if (!cancelled)
                setFooterValues(newFooterValues)
        }

        calculateFooters()

        return () => {
            cancelled = true
        }
    }, [calculatedColumnsKey, appliedFilters])

    const hasFooter = columns.some(col => col.footer)

    if (!hasFooter)
        return null

    return (
        <StyledFooterRow>
            {leadingCells.map((cell, index) => (
                <RadixTable.Cell key={cell} style={leadingCellStyle(index)} />
            ))}
            {columns.map((column, index) => {
                const rawValue = footerValues.get(column.accessor as string)
                const formattedValue = column.footer?.formatter
                    ? column.footer.formatter(rawValue)
                    : rawValue

                return (
                    <RadixTable.Cell key={index} style={columnStyle(index)}>
                        {formattedValue ?? ''}
                    </RadixTable.Cell>
                )
            })}
            {hasActionsColumn && <RadixTable.Cell />}
        </StyledFooterRow>
    )
}

export default TableSummaryRow
